import { BaseApiClient } from '../base-client'
import {
  API_ROUTES,
  ApiResponse,
  ApiKeyCredentialIntegrationType,
  interpolateRoute,
  type RentvineProperty,
} from '@super/shared-types'
import { withErrorHandling } from '../../utils'

export interface RentvineMetadata {
  defaultPropertyID?: string
  /** @deprecated Use defaultPropertyID instead */
  defaultPropertyAddress?: string
  defaultWorkOrderStatusID?: number
}

/**
 * Rentvine Integration API Client
 * Handles all Rentvine-specific API operations
 * Uses HTTP Basic Auth with accessKey + secret + accountId
 */
export class RentvineClient extends BaseApiClient {
  /**
   * Create API key credentials for Rentvine
   * @param accessKey - The API access key (maps to clientId)
   * @param secret - The API secret (maps to clientSecret)
   * @param accountId - The Rentvine account subdomain (e.g., "demoaccount74")
   * @param metadata - Optional work order defaults (defaultPropertyID, defaultWorkOrderStatusID)
   */
  async createCredentials(
    accessKey: string,
    secret: string,
    accountId: string,
    metadata?: RentvineMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
        )
        const body: Record<string, unknown> = {
          clientId: accessKey,
          clientSecret: secret,
          accountId,
        }
        if (metadata && Object.keys(metadata).length > 0)
          body.metadata = metadata
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'POST',
          body: JSON.stringify(body),
        })
      },
      {
        errorContext: 'Error creating Rentvine API credentials',
      },
    )
  }

  /**
   * [ADMIN] Create API key credentials for Rentvine for a specific company
   */
  async createAdminCredentials(
    companyId: string,
    accessKey: string,
    secret: string,
    accountId: string,
    metadata?: RentvineMetadata,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.CREATE,
          { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
        )
        const body: Record<string, unknown> = {
          clientId: accessKey,
          clientSecret: secret,
          accountId,
        }
        if (metadata && Object.keys(metadata).length > 0)
          body.metadata = metadata
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          },
        )
      },
      {
        errorContext: 'Error creating admin Rentvine API credentials',
      },
    )
  }

  /**
   * Update API key credentials for Rentvine
   * clientSecret is optional when only updating metadata or other fields (leave blank to keep current)
   */
  async updateCredentials(
    accessKey: string,
    accountId: string,
    options?: { clientSecret?: string; metadata?: RentvineMetadata },
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
        )
        const body: Record<string, unknown> = {
          clientId: accessKey,
          accountId,
        }
        if (options?.clientSecret) body.clientSecret = options.clientSecret
        if (options?.metadata && Object.keys(options.metadata).length > 0) {
          body.metadata = options.metadata
        }
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
      },
      {
        errorContext: 'Error updating Rentvine API credentials',
      },
    )
  }

  /**
   * [ADMIN] Update API key credentials for Rentvine for a specific company
   * clientSecret is optional when only updating metadata or other fields (leave blank to keep current)
   */
  async updateAdminCredentials(
    companyId: string,
    accessKey: string,
    accountId: string,
    options?: { clientSecret?: string; metadata?: RentvineMetadata },
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        const url = interpolateRoute(
          API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
          { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
        )
        const body: Record<string, unknown> = {
          clientId: accessKey,
          accountId,
        }
        if (options?.clientSecret) body.clientSecret = options.clientSecret
        if (options?.metadata && Object.keys(options.metadata).length > 0) {
          body.metadata = options.metadata
        }
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify(body),
          },
        )
      },
      {
        errorContext: 'Error updating admin Rentvine API credentials',
      },
    )
  }

  /**
   * Update Rentvine metadata (defaultPropertyID, defaultWorkOrderStatusID)
   * Uses the generic API key credentials update endpoint with metadata only
   */
  async updateMetadata(metadata: RentvineMetadata): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'PUT',
          body: JSON.stringify({ metadata }),
        })
      },
      {
        errorContext: 'Error updating Rentvine metadata',
      },
    )
  }

  /**
   * [ADMIN] Update Rentvine metadata for a specific company
   */
  async updateAdminMetadata(
    companyId: string,
    metadata: RentvineMetadata,
  ): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.UPDATE,
      { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ metadata }),
          },
        )
      },
      {
        errorContext: 'Error updating admin Rentvine metadata',
      },
    )
  }

  async getProperties(): Promise<RentvineProperty[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<RentvineProperty[]>>(
          API_ROUTES.INTEGRATIONS.RENTVINE.PROPERTIES,
        )
        return response.data || []
      },
      { errorContext: 'Error fetching Rentvine properties' },
    )
    return result || []
  }

  async getAdminProperties(companyId: string): Promise<RentvineProperty[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<RentvineProperty[]>>(
          `${API_ROUTES.ADMIN.INTEGRATIONS.RENTVINE.PROPERTIES}?companyId=${companyId}`,
        )
        return response.data || []
      },
      { errorContext: 'Error fetching Rentvine properties' },
    )
    return result || []
  }

  /**
   * Disconnect Rentvine integration
   */
  async disconnect(): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(url, {
          method: 'DELETE',
        })
      },
      {
        errorContext: 'Error disconnecting Rentvine integration',
      },
    )
  }

  /**
   * [ADMIN] Disconnect Rentvine integration for a specific company
   */
  async disconnectAdmin(companyId: string): Promise<void> {
    const url = interpolateRoute(
      API_ROUTES.ADMIN.INTEGRATIONS.API_KEY_CREDENTIALS.DELETE,
      { integrationType: ApiKeyCredentialIntegrationType.RENTVINE },
    )
    await withErrorHandling(
      async () => {
        return await this.request<ApiResponse<{ message: string }>>(
          `${url}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
      },
      {
        errorContext: 'Error disconnecting admin Rentvine integration',
      },
    )
  }
}

// Export singleton instance
export const rentvineClient = new RentvineClient()
